import { useEffect, useRef, useState } from "react";
import { Building2, CheckCircle2, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

// Company name field backed by the NZ business register (NZBN). value = { name, nzbn, manual }
export function CompanyRegisterPicker({ value, onChange }) {
  const [q, setQ] = useState(value.name || "");
  const [results, setResults] = useState([]);
  const [state, setState] = useState("idle"); // idle | searching | done | unavailable
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (value.nzbn || value.manual || q.trim().length < 2) { setResults([]); if (!value.manual) setState("idle"); return undefined; }
    timer.current = setTimeout(async () => {
      setState("searching");
      try {
        const { data } = await api.get("/signup/business-search", { params: { q: q.trim() }, __flSkipReadCache: true });
        if (!data.available) { setState("unavailable"); setResults([]); onChange({ name: q, nzbn: null, manual: true }); return; }
        setResults(data.results || []); setState("done"); setOpen(true);
      } catch { setState("unavailable"); setResults([]); onChange({ name: q, nzbn: null, manual: true }); }
    }, 400);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, value.nzbn, value.manual]);

  const pick = (r) => { onChange({ name: r.name, nzbn: r.nzbn, manual: false }); setQ(r.name); setOpen(false); };
  const reset = () => { onChange({ name: "", nzbn: null, manual: false }); setQ(""); };

  if (value.nzbn) {
    return (
      <div className="mt-2 flex items-center gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5" data-testid="register-company-verified">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{value.name}</p>
          <p className="text-xs text-emerald-800">Found on the NZ business register · NZBN {value.nzbn}</p>
        </div>
        <button type="button" onClick={reset} className="text-xs font-semibold text-[#FF5F15] hover:underline" data-testid="register-company-change">Change</button>
      </div>
    );
  }

  return (
    <div className="mt-2 relative">
      <Building2 className="absolute left-3 top-6 -translate-y-1/2 w-4 h-4 text-slate-600" />
      <Input required value={q} onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); onChange({ name: e.target.value, nzbn: null, manual: value.manual }); }}
        className="h-12 rounded-md bg-white border-slate-300 text-[#142635] placeholder:text-slate-400 pl-10 pr-10"
        placeholder="Start typing your registered business name" autoComplete="off" data-testid="register-company-input" />
      {state === "searching" ? <Loader2 className="absolute right-3 top-6 -translate-y-1/2 w-4 h-4 animate-spin text-slate-500" />
        : <Search className="absolute right-3 top-6 -translate-y-1/2 w-4 h-4 text-slate-400" />}
      {open && state === "done" && !value.manual && (
        <ul className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid="register-company-results">
          {results.length === 0 && <li className="px-3 py-3 text-sm text-slate-500">No registered business matches that name.</li>}
          {results.map((r) => (
            <li key={r.nzbn}>
              <button type="button" disabled={!r.registered} onClick={() => pick(r)} data-testid={`register-company-result-${r.nzbn}`}
                className="w-full text-left px-3 py-2.5 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <p className="text-sm font-semibold">{r.name}</p>
                <p className="text-xs text-slate-500">{r.type} · NZBN {r.nzbn}{!r.registered && ` · ${r.status}, can't be used`}</p>
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.manual || state === "unavailable" ? (
        <p className="mt-2 text-xs text-amber-700" data-testid="register-company-manual-note">
          {state === "unavailable" ? "The NZ business register can't be reached right now. " : ""}We'll check your company by hand before your workspace opens, usually within one business day.
          {value.manual && <button type="button" onClick={() => onChange({ ...value, manual: false })} className="ml-1 font-semibold text-[#FF5F15] hover:underline">Search again</button>}
        </p>
      ) : (
        <button type="button" onClick={() => { onChange({ name: q, nzbn: null, manual: true }); setOpen(false); }} className="mt-2 text-xs font-semibold text-slate-600 hover:text-[#FF5F15]" data-testid="register-company-cant-find">
          Can't find your business?
        </button>
      )}
    </div>
  );
}
